import {
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { CreatorResponseDto } from '../../../../../common/general-types/creators.dto';
import { Subscription } from 'rxjs';
import { VideoResponseWithBriefDto } from '../../../../../common/general-types/videos.dto';
import { VideoService } from '../../../../../services/video/video.service';
import { ImageService } from '../../../../../services/image/image.service';
import { LoadingInlineComponent } from '../../../../../atoms/loading-inline/loading-inline.component';
import { MagicSearchService } from '../../../service/magic-search/magic-search.service';
import { SingleVideoViewerSummaryComponent } from '../../../../../atoms/single-video-viewer-summary/single-video-viewer-summary.component';
import { MgcPortfolioInlineVideoComponent } from './mgc-portfolio-inline-video.component';
import {
  filterPortfolioVideos,
  portfolioBrandName,
  portfolioBrandOptions,
  portfolioPlatformLabel,
} from '../creator-profile.helpers';

@Component({
  selector: 'mgc-portfolio',
  standalone: true,
  imports: [LoadingInlineComponent, MgcPortfolioInlineVideoComponent, SingleVideoViewerSummaryComponent],
  templateUrl: './mgc-portfolio.component.html',
  styleUrl: './mgc-portfolio.component.css'
})
export class MgcPortfolioComponent implements OnInit, OnChanges, OnDestroy {

  @Input() creator!: CreatorResponseDto;

  readonly videoCount = output<number>();

  creatorVideos = signal<VideoResponseWithBriefDto[] | undefined>(undefined);
  readonly brandFilter = signal('All');
  readonly brandNames = computed(() => portfolioBrandOptions(this.creatorVideos() ?? []));
  readonly visibleVideos = computed(() => filterPortfolioVideos(this.creatorVideos() ?? [], this.brandFilter()));
  subscriptionList = new Subscription();

  previewVideo = signal<VideoResponseWithBriefDto | undefined>(undefined);

  private videoService = inject(VideoService);
  public imageService = inject(ImageService);
  public magicSearchService = inject(MagicSearchService);
  protected readonly portfolioPlatformLabel = portfolioPlatformLabel;
  protected readonly portfolioBrandName = portfolioBrandName;

  private loadedCreatorId: string | undefined;

  ngOnInit(): void {
    this.getCreatorVideos();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['creator'] || changes['creator'].firstChange) {
      return;
    }
    const nextId = this.creator?.blazeFirebaseUid;
    if (!nextId || nextId === this.loadedCreatorId) {
      return;
    }
    this.getCreatorVideos();
  }

  selectBrand(brand: string): void {
    this.brandFilter.set(brand);
  }

  brandChipClass(brand: string): string {
    const base = 'btn-shout btn-shout-xs shrink-0';
    return this.brandFilter() === brand ? `${base} btn-shout-primary` : `${base} btn-shout-secondary`;
  }

  getCreatorVideos() {
    if (!this.creator?.blazeFirebaseUid) {
      return;
    }

    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();
    this.loadedCreatorId = this.creator.blazeFirebaseUid;
    this.brandFilter.set('All');
    this.creatorVideos.set(undefined);
    this.previewVideo.set(undefined);

    const sub = this.videoService.getCreatorVideosBrief(this.creator.blazeFirebaseUid).subscribe({
      next: (videoRes) => {
        const videos = videoRes as VideoResponseWithBriefDto[];
        this.creatorVideos.set(videos);
        const names = portfolioBrandOptions(videos);
        if (this.brandFilter() !== 'All' && !names.includes(this.brandFilter())) {
          this.brandFilter.set('All');
        }
        this.videoCount.emit(videos.length);
      },
      error: (err) => {
        console.warn('Error getting creator videos: ', err);
        this.creatorVideos.set([]);
        this.videoCount.emit(0);
      }
    });

    this.subscriptionList.add(sub);
  }

  showVideoPreview(video: VideoResponseWithBriefDto) {
    this.previewVideo.set(video);
  }

  ngOnDestroy() {
    this.subscriptionList.unsubscribe();
  }
}
